import { useMemo, useState } from 'react';
import {
  CheckCircle2,
  KeyRound,
  RefreshCw,
  Server,
  ShieldAlert,
  Trash2,
  XCircle,
} from 'lucide-react';
import type { ApiLogEntry } from '@mytag/shared';
import { mytagApi } from '@/services/mytagApi';
import { useDebugConfig, useDebugLogs } from '@/hooks/queries';
import { PageHeader } from '@/components/layout/AppShell';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Field,
  LoadingState,
  Select,
  Table,
  Td,
  Th,
} from '@/components/ui/primitives';
import { ErrorState } from '@/components/common/ErrorState';
import { CopyButton, DataRow } from '@/components/common/indicators';
import { formatDateTime } from '@/lib/format';

export function DebugPage(): JSX.Element {
  const [limit, setLimit] = useState(50);
  const logsQuery = useDebugLogs(limit);
  const configQuery = useDebugConfig();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [busy, setBusy] = useState<'clear' | 'reset' | null>(null);

  const logs = useMemo(() => logsQuery.data?.logs ?? [], [logsQuery.data]);
  const crypto = configQuery.data?.crypto;

  const cryptoErrors = useMemo(
    () => logs.filter((log) => log.error?.code?.startsWith('CRYPTO')),
    [logs],
  );

  const fullDiagnostic = useMemo(
    () =>
      JSON.stringify(
        {
          generatedAt: new Date().toISOString(),
          config: configQuery.data ?? null,
          logs: logs.slice(0, 20),
        },
        null,
        2,
      ),
    [configQuery.data, logs],
  );

  async function runAction(action: 'clear' | 'reset'): Promise<void> {
    setBusy(action);
    try {
      if (action === 'clear') await mytagApi.clearDebugLogs();
      else await mytagApi.resetMockData();
      await logsQuery.refetch();
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <PageHeader
        title="Debug / Logs API"
        description="Échanges avec l'API Vernal, assainis : aucun secret n'est affiché."
        actions={
          <>
            <CopyButton value={fullDiagnostic} label="Copier le diagnostic" size="md" />
            <Button
              variant="outline"
              icon={<RefreshCw className="h-4 w-4" />}
              loading={logsQuery.isFetching}
              onClick={() => void logsQuery.refetch()}
            >
              Recharger
            </Button>
            <Button
              variant="outline"
              icon={<Trash2 className="h-4 w-4" />}
              loading={busy === 'clear'}
              onClick={() => void runAction('clear')}
            >
              Vider les logs
            </Button>
          </>
        }
      />

      {configQuery.isError ? (
        <ErrorState error={configQuery.error} onRetry={() => void configQuery.refetch()} />
      ) : null}

      {cryptoErrors.length > 0 ? (
        <Alert tone="danger" title={`${cryptoErrors.length} erreur(s) de chiffrement/déchiffrement`}>
          <ul className="list-inside list-disc space-y-1">
            {cryptoErrors.slice(0, 3).map((log) => (
              <li key={log.id}>
                <code className="font-mono">{log.error?.code}</code> — {log.error?.message}
              </li>
            ))}
          </ul>
        </Alert>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-muted-foreground" aria-hidden />
              Chiffrement Triple DES
            </CardTitle>
          </CardHeader>
          <CardContent>
            {crypto ? (
              <>
                <div className="mb-3">
                  {crypto.selfTest.ok ? (
                    <Badge tone="success">
                      <CheckCircle2 className="h-3 w-3" aria-hidden />
                      Auto-test réussi
                    </Badge>
                  ) : (
                    <Badge tone="danger">
                      <XCircle className="h-3 w-3" aria-hidden />
                      Auto-test en échec
                    </Badge>
                  )}
                </div>
                <dl className="divide-y divide-border">
                  <DataRow label="Configuré" value={crypto.configured ? 'Oui' : 'Non'} />
                  <DataRow label="Mode" value={crypto.mode} />
                  <DataRow label="Padding" value={crypto.padding} />
                  <DataRow label="Origine de la clé" value={crypto.keySource} />
                  <DataRow label="Format" value={crypto.keyFormat} />
                  <DataRow label="Stratégie" value={crypto.keyStrategy} />
                  <DataRow label="Longueur dérivée" value={`${crypto.derivedKeyLength} octets`} />
                  <DataRow label="Empreinte clé" value={crypto.keyPreview ?? '—'} mono />
                </dl>
                {!crypto.selfTest.ok ? (
                  <Alert tone="warning" className="mt-3" title="Diagnostic">
                    {crypto.selfTest.message}
                  </Alert>
                ) : null}
              </>
            ) : (
              <LoadingState label="Lecture de la configuration…" />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="h-4 w-4 text-muted-foreground" aria-hidden />
              Backend & API Vernal
            </CardTitle>
          </CardHeader>
          <CardContent>
            {configQuery.data ? (
              <dl className="divide-y divide-border">
                <DataRow label="Environnement" value={configQuery.data.nodeEnv} />
                <DataRow
                  label="Mode mock serveur"
                  value={
                    configQuery.data.mockMode ? <Badge tone="warning">Activé</Badge> : <Badge>Désactivé</Badge>
                  }
                />
                <DataRow label="Base URL" value={configQuery.data.vernal.baseUrl} mono />
                <DataRow label="Client code" value={configQuery.data.vernal.clientCode} mono />
                <DataRow label="CID (masqué)" value={configQuery.data.vernal.cid ?? '—'} mono />
                <DataRow label="Timeout" value={`${configQuery.data.vernal.timeoutMs} ms`} />
                <DataRow label="Tentatives" value={configQuery.data.vernal.retries + 1} />
                <DataRow label="Enveloppe" value={configQuery.data.vernal.envelopeMode} mono />
                <DataRow
                  label="Codes de succès"
                  value={configQuery.data.vernal.successCodes.join(', ')}
                  mono
                />
                <DataRow
                  label="Déchiffrer réponses"
                  value={configQuery.data.vernal.decryptResponse ? 'Oui' : 'Non'}
                />
              </dl>
            ) : (
              <LoadingState />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-muted-foreground" aria-hidden />
              Outils
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p className="text-xs text-muted-foreground">
              Les valeurs sensibles (mot de passe, token, CID, clé) sont masquées à la source, côté
              backend, avant journalisation.
            </p>
            <Field label="Nombre de logs affichés" htmlFor="limit">
              <Select
                id="limit"
                value={String(limit)}
                onChange={(event) => setLimit(Number(event.target.value))}
              >
                {[25, 50, 100, 200].map((value) => (
                  <option key={value} value={value}>
                    {value} entrées
                  </option>
                ))}
              </Select>
            </Field>
            <Button
              variant="outline"
              className="w-full"
              loading={busy === 'reset'}
              icon={<RefreshCw className="h-4 w-4" />}
              onClick={() => void runAction('reset')}
            >
              Réinitialiser les données mock
            </Button>
            <p className="text-xs text-muted-foreground">
              {logsQuery.data ? `${logsQuery.data.total} entrée(s) en mémoire côté backend.` : ''}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Journal des appels</CardTitle>
        </CardHeader>
        <CardContent className="p-0 sm:p-0">
          {logsQuery.isLoading ? (
            <LoadingState label="Chargement des logs…" />
          ) : logsQuery.isError ? (
            <div className="p-4">
              <ErrorState error={logsQuery.error} onRetry={() => void logsQuery.refetch()} />
            </div>
          ) : logs.length === 0 ? (
            <EmptyState
              title="Aucun appel enregistré"
              description="Se connecter puis rafraîchir une position pour alimenter le journal."
            />
          ) : (
            <Table className="min-w-[860px]">
              <thead>
                <tr>
                  <Th>Horodatage</Th>
                  <Th>Endpoint</Th>
                  <Th>HTTP</Th>
                  <Th>Code API</Th>
                  <Th>Durée</Th>
                  <Th>Source</Th>
                  <Th className="text-right">Détail</Th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <LogRow
                    key={log.id}
                    log={log}
                    expanded={expanded === log.id}
                    onToggle={() => setExpanded(expanded === log.id ? null : log.id)}
                  />
                ))}
              </tbody>
            </Table>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function LogRow({
  log,
  expanded,
  onToggle,
}: {
  log: ApiLogEntry;
  expanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  const diagnostic = JSON.stringify(log, null, 2);

  return (
    <>
      <tr className={log.success ? 'hover:bg-muted/40' : 'bg-danger/5 hover:bg-danger/10'}>
        <Td className="whitespace-nowrap text-xs">{formatDateTime(log.timestamp)}</Td>
        <Td className="font-mono text-xs">
          {log.endpoint}
          {log.mock ? (
            <Badge tone="warning" className="ml-2">
              mock
            </Badge>
          ) : null}
        </Td>
        <Td>
          <Badge tone={log.statusCode && log.statusCode < 400 ? 'success' : log.statusCode ? 'danger' : 'neutral'}>
            {log.statusCode ?? '—'}
          </Badge>
        </Td>
        <Td className="tabular text-xs">
          {log.apiCode ?? '—'}
          {log.apiMessage ? <span className="ml-1 text-muted-foreground">{log.apiMessage}</span> : null}
        </Td>
        <Td className="tabular text-xs">{log.durationMs} ms</Td>
        <Td className="text-xs text-muted-foreground">{log.source ?? '—'}</Td>
        <Td className="text-right">
          <Button size="sm" variant="ghost" onClick={onToggle}>
            {expanded ? 'Masquer' : 'Voir'}
          </Button>
        </Td>
      </tr>
      {expanded ? (
        <tr>
          <Td colSpan={7} className="bg-muted/30">
            <div className="space-y-3 py-2">
              {log.error ? (
                <Alert tone="danger" title={`${log.error.code} — ${log.error.message}`}>
                  {log.error.details ? (
                    <pre className="scroll-thin mt-1 max-h-48 overflow-auto rounded bg-card p-2 font-mono text-[11px]">
                      {JSON.stringify(log.error.details, null, 2)}
                    </pre>
                  ) : null}
                </Alert>
              ) : null}
              <div className="grid gap-3 lg:grid-cols-2">
                <PayloadBlock title="Requête (assainie)" value={log.request} />
                <PayloadBlock title="Réponse (assainie)" value={log.response} />
              </div>
              <CopyButton value={diagnostic} label="Copier cette entrée" />
            </div>
          </Td>
        </tr>
      ) : null}
    </>
  );
}

function PayloadBlock({ title, value }: { title: string; value: unknown }): JSX.Element {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium">{title}</p>
      <pre className="scroll-thin max-h-64 overflow-auto rounded-md border border-border bg-card p-2 font-mono text-[11px] leading-relaxed">
        {value === null || value === undefined ? '—' : JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}
